import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Check,
  Star,
  Crown,
  Zap,
  Sparkles,
  Camera,
  Eye,
  BarChart3,
  Shield,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

function PremiumPage() {
  const { user, isBartender, setUser } = useAuth();
  const [loading, setLoading] = useState({});
  const [packages, setPackages] = useState(null);
  const [loadingPackages, setLoadingPackages] = useState(true);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get('/premium/packages');
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
      toast.error('Failed to load pricing packages');
    } finally {
      setLoadingPackages(false);
    }
  };

  const betaPlanLabels = {
    premium_bartender_monthly: 'Monthly Free Beta Lock-In',
    premium_bartender_yearly: 'Yearly Free Beta Lock-In',
  };

  const handleSelectPlan = async (packageId) => {
    setLoading(prev => ({ ...prev, [packageId]: true }));
    
    try {
      const response = await axios.post('/beta/lock-in-plan', {
        plan_id: packageId,
      });
      if (response.data?.user) {
        setUser(response.data.user);
      }
      toast.success(response.data?.message || 'Free Beta Status Active');
    } catch (error) {
      console.error('Failed to lock in beta plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to lock in free beta status');
    } finally {
      setLoading(prev => ({ ...prev, [packageId]: false }));
    }
  };

  if (loadingPackages) {
    return (
      <div className="container max-w-7xl mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-muted-foreground">Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  // If user is already premium
  if (user?.is_premium) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="premium-page">
        <div className="text-center mb-8">
          <Crown className="h-16 w-16 mx-auto mb-4 text-primary fill-current" />
          <h1 className="text-4xl font-bold text-white mb-2 font-['Barlow_Condensed'] uppercase">
            You&apos;re Premium!
          </h1>
          <p className="text-lg text-primary/80">
            Free Beta Status Active — enjoy premium features during beta
          </p>
          <p className="text-sm text-muted-foreground mt-2" data-testid="beta-plan-status-text">
            {betaPlanLabels[user?.beta_plan_id] || 'Beta lock-in active'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-primary/10 to-[#F5C15D]/10 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center font-['Barlow_Condensed'] uppercase">
                <Star className="h-5 w-5 mr-2 text-primary" />
                Premium Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-green-400 font-semibold mb-2">Active Premium Member</p>
              <p className="text-sm text-muted-foreground">
                Your free beta lock-in gives you enhanced visibility, priority matching, and advanced features.
              </p>
              <div className="mt-4">
                <Button asChild className="w-full">
                  <Link to="/dashboard">View Dashboard Insights</Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-md border border-white/5">
            <CardHeader>
              <CardTitle className="flex items-center font-['Barlow_Condensed'] uppercase">
                <BarChart3 className="h-5 w-5 mr-2 text-primary" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button asChild variant="outline" className="w-full justify-start">
                <Link to="/profile">
                  <Camera className="h-4 w-4 mr-2" />
                  Upload Video Introduction
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-start">
                <Link to="/profile">
                  <Eye className="h-4 w-4 mr-2" />
                  Manage Portfolio Gallery
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full justify-start">
                <Link to="/verification">
                  <Shield className="h-4 w-4 mr-2" />
                  Request Verification
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Redirect non-bartenders
  if (!isBartender) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
        <Card className="bg-white/5 backdrop-blur-md border border-white/5 max-w-md mx-auto">
          <CardContent className="p-8">
            <Crown className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Premium Profiles</h2>
            <p className="text-muted-foreground mb-4">
              Premium bartender profiles are available for bartenders only.
            </p>
            <p className="text-sm text-muted-foreground">
              Bar owners can access premium job posting features through our pricing page.
            </p>
            <div className="mt-6">
              <Button asChild>
                <Link to="/pricing">View Bar Owner Plans</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const premiumFeatures = [
    {
      icon: Star,
      title: "Featured Profile Placement",
      description: "Your profile appears at the top of search results with a premium badge"
    },
    {
      icon: Zap,
      title: "Priority Job Matching",
      description: "Get notified first about jobs that match your skills and preferences"
    },
    {
      icon: Camera,
      title: "Video Introduction",
      description: "Upload a personal video to showcase your personality and skills"
    },
    {
      icon: Eye,
      title: "Portfolio Gallery",
      description: "Showcase up to 20 professional photos of your work and creations"
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      description: "Track profile views, match rates, and optimize your visibility"
    },
    {
      icon: Shield,
      title: "Verification Badges",
      description: "Access to ID verification, skill certification, and background checks"
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="premium-page">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <Crown className="h-12 w-12 text-primary fill-current mr-3" />
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white font-['Barlow_Condensed']">
            Premium Bartender
          </h1>
        </div>
        <p className="text-xl text-muted-foreground mb-2">
          Stand out from the crowd and get noticed by top bar owners
        </p>
        <p className="text-sm text-primary/80">
          Lock in free beta access now — no payment required during beta.
        </p>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 max-w-4xl mx-auto">
        {/* Monthly Plan */}
        <Card className="relative bg-white/5 backdrop-blur-md border border-white/5 hover:border-primary/30 transition-all duration-300">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <Star className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="font-['Barlow_Condensed'] uppercase text-lg">
              Monthly Premium
            </CardTitle>
            <CardDescription className="text-sm">
              Perfect for trying premium features
            </CardDescription>
            <div className="mt-4">
              <div className="text-4xl font-bold text-white">
                $29<span className="text-lg text-muted-foreground">.99</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">per month</p>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <Button
              onClick={() => handleSelectPlan('premium_bartender_monthly')}
              disabled={loading['premium_bartender_monthly']}
              className="w-full bg-primary text-white hover:bg-[hsl(var(--primary-light))]"
              data-testid="select-monthly-premium"
            >
              {loading['premium_bartender_monthly'] ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-current mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Lock In Free Beta (Monthly)
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Yearly Plan */}
        <Card className="relative bg-gradient-to-br from-primary/10 to-[#F5C15D]/10 border-primary/20 shadow-lg scale-105">
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <Badge className="bg-primary text-white px-4 py-1">
              <Star className="h-3 w-3 mr-1 fill-current" />
              Best Value - Save 17%
            </Badge>
          </div>
          
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-lg bg-primary/20">
                <Crown className="h-8 w-8 text-primary fill-current" />
              </div>
            </div>
            <CardTitle className="font-['Barlow_Condensed'] uppercase text-lg">
              Yearly Premium
            </CardTitle>
            <CardDescription className="text-sm">
              Best value for serious bartenders
            </CardDescription>
            <div className="mt-4">
              <div className="flex items-center justify-center space-x-2">
                <span className="text-4xl font-bold text-white">
                  $299<span className="text-lg text-muted-foreground">.99</span>
                </span>
                <div className="text-right">
                  <div className="text-sm text-muted-foreground line-through">$359.88</div>
                  <div className="text-xs text-green-400">Save $59.89</div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1">per year</p>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <Button
              onClick={() => handleSelectPlan('premium_bartender_yearly')}
              disabled={loading['premium_bartender_yearly']}
              className="w-full bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-light))] shadow-[0_0_20px_-5px_rgba(255,45,138,0.5)]"
              data-testid="select-yearly-premium"
            >
              {loading['premium_bartender_yearly'] ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-current mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Crown className="h-4 w-4 mr-2" />
                  Lock In Free Beta (Yearly)
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Features Grid */}
      <Card className="bg-white/5 backdrop-blur-md border border-white/5 mb-12">
        <CardHeader className="text-center">
          <CardTitle className="font-['Barlow_Condensed'] uppercase">
            Premium Features
          </CardTitle>
          <CardDescription>
            Everything you need to stand out and get hired faster
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {premiumFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="flex items-start space-x-4 p-4 rounded-lg border border-white/10 hover:border-primary/30 transition-colors">
                  <div className="flex-shrink-0">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Success Stories */}
      <Card className="bg-gradient-to-r from-green-500/10 to-primary/10 border-green-500/20 mb-12">
        <CardHeader className="text-center">
          <CardTitle className="font-['Barlow_Condensed'] uppercase">
            Premium Success Stories
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400 mb-2">Free</div>
              <p className="text-sm text-muted-foreground">No payment required during beta</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">Direct</div>
              <p className="text-sm text-muted-foreground">Connect with bar owners through in-platform messaging</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary mb-2">Beta</div>
              <p className="text-sm text-muted-foreground">Early access to premium profile features</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <Card className="bg-white/5 backdrop-blur-md border border-white/5">
        <CardHeader className="text-center">
          <CardTitle className="font-['Barlow_Condensed'] uppercase">
            Frequently Asked Questions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-white mb-2">What makes premium profiles special?</h3>
              <p className="text-sm text-muted-foreground">
                Premium profiles appear first in search results, get priority notifications for matching jobs, and have access to advanced features like video introductions and detailed analytics.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">Can I cancel anytime?</h3>
              <p className="text-sm text-muted-foreground">
                Yes! You can cancel your premium subscription at any time. You&apos;ll continue to have access to premium features until your current billing period ends.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">How does verification work?</h3>
              <p className="text-sm text-muted-foreground">
                Premium members can request ID verification, skill certification, and background checks. These badges increase trust and credibility with bar owners.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-2">What payment methods do you accept?</h3>
              <p className="text-sm text-muted-foreground">
                During free beta, no payment method is required. Stripe checkout is disabled until paid plans launch.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default PremiumPage;
